/**
 * W93-Krypto — Lese-Seite des Krypto-Boden-Scores.
 *
 * Der Server-Score-Cron (18:15) berechnet computeCryptoBodenScore über
 * 1500-Bar-Langhistorie (200W-MA, Zyklus-Fibs, Halving-Uhr) und schreibt
 * nach v5_crypto_boden. Der Browser liest hier NUR — die Langhistorie wäre
 * im Client zu teuer (1500 Bars × N Coins bei jedem Watchlist-Render).
 *
 * Eine Query für ALLE Symbole (Tabelle hat ~10 Rows) — alle Krypto-Zellen
 * teilen sich den TanStack-Cache über denselben queryKey.
 */

import { supabase } from '@/lib/supabase';

export interface CryptoBodenRow {
  symbol: string;
  boden_score: number;
  trend_score: number | null;
  fib_states: Array<{
    anchor: 'app' | 'full_cycle' | 'ma200w';
    ratio: number;
    level_price: number;
    state: string;
    hint_de: string;
  }>;
  t1_ready: boolean;
  falling_knife: boolean;
  summary: string | null;
  invalidation: string | null;
  levels: {
    ma200w?: number;
    ma200d?: number;
    ath?: number;
    ath_date?: string;
    run_low_app?: number;
    run_low_full?: number;
  } | null;
  updated_at: string;
}

export async function fetchCryptoBodenAll(
  signal?: AbortSignal
): Promise<Map<string, CryptoBodenRow>> {
  let query = supabase.from('v5_crypto_boden').select('*');
  if (signal) query = query.abortSignal(signal);
  const { data, error } = await query;
  if (error) throw new Error(`v5_crypto_boden: ${error.message}`);
  const map = new Map<string, CryptoBodenRow>();
  for (const row of (data ?? []) as CryptoBodenRow[]) {
    map.set(row.symbol.toUpperCase(), row);
  }
  return map;
}

/**
 * Anzeige-Reihenfolge der Zonen: aktive Reaktionszonen (test/verteidigt/
 * reclaim) vor Annäherungen vor Gebrochenem; bei Gleichstand 200W-MA zuerst
 * (stärkste Basisrate), dann tiefere Fibs. Ohne Sortierung würde slice(0,3)
 * die Push-Reihenfolge zeigen und z.B. den aktiven 200W-MA-Test abschneiden.
 */
export function sortFibStatesForDisplay(
  states: CryptoBodenRow['fib_states']
): CryptoBodenRow['fib_states'] {
  const prio: Record<string, number> = {
    test: 0,
    verteidigt: 0,
    reclaim: 1,
    annaeherung: 2,
    durchgefallen: 3,
  };
  return [...states].sort((a, b) => {
    const pa = prio[a.state] ?? 9;
    const pb = prio[b.state] ?? 9;
    if (pa !== pb) return pa - pb;
    const ma = a.anchor === 'ma200w' ? 0 : 1;
    const mb = b.anchor === 'ma200w' ? 0 : 1;
    if (ma !== mb) return ma - mb;
    return b.ratio - a.ratio;
  });
}

/** Farbe nach Akkumulations-Logik: hoch = historisch gute Kaufzone. */
export function bodenBadgeClass(score: number, fallingKnife: boolean): string {
  if (fallingKnife) return 'border-red-700/60 bg-red-900/30 text-red-300';
  if (score >= 80) return 'border-emerald-700/60 bg-emerald-900/30 text-emerald-300';
  if (score >= 65) return 'border-orange-700/60 bg-orange-900/30 text-orange-300';
  if (score >= 50) return 'border-amber-700/60 bg-amber-900/30 text-amber-300';
  return 'border-zinc-700 bg-zinc-800/60 text-zinc-400';
}
